import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState } from "react";
import Service from "./../webservice/http";
import { useHistory } from "react-router-dom";
import { storage } from "../utils/storage";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ReportFilter from "./report-filter";
import { FaFilter } from "react-icons/fa";
import ReportFilterBanner from "./report-filter-banner";

const Recovery = () => {
  const history = useHistory();
  const services = new Service();
  const role = storage.getItem("role");
  const isAgentRole =
    role === "Agent" ||
    role === "Agenth" ||
    role === "Agent Collections" ||
    role.includes("Agent");

  // ── Recovery Bar Chart ──────────────────────────────────────────────────────
  const [recoveryDateLabels, setRecoveryDateLabels] = useState([]);
  const [recoveryStackedSeries, setRecoveryStackedSeries] = useState([]);

  const formatDMY = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  async function fetchRecoveryIntentData(
    fromDate,
    toDate,
    collectionTypeOverride
  ) {
    let url = `api/report/promise-to-pay-trend/`;
    const params = [];

    // Use the override if provided (avoids stale closure when called from handler)
    const activeType =
      collectionTypeOverride !== undefined
        ? collectionTypeOverride
        : collectionType;
    if (activeType) {
      params.push(`due_category=${activeType}`);
    }
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    try {
      const res = await services.get(url);
      if (!res) return;
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      const items = res.date_wise_trend || [];
      const makeData = (key) => items.map((item) => item[key] ?? 0);

      // Compute scale so collected_amount doesn't crush the other series visually
      const otherMax = Math.max(
        1,
        ...items.flatMap((item) => [
          item.promise_to_pay ?? 0,
          item.ptp_rate ?? 0,
          item.kept_rate ?? 0,
        ])
      );
      const collectedMax = Math.max(
        1,
        ...items.map((item) => item.collected_amount ?? 0)
      );
      // Scale so displayed collected_amount max ≈ 2× other-series max
      const collectedScale = Math.ceil(collectedMax / (otherMax * 2));

      setRecoveryDateLabels(items.map((item) => item.date));
      setRecoveryStackedSeries([
        {
          name: "PTP Count",
          color: "#7cb5ec",
          data: makeData("promise_to_pay"),
        },
        { name: "PTP Rate", color: "#4ecdc4", data: makeData("ptp_rate") },
        { name: "Kept Rate", color: "#f7a35c", data: makeData("kept_rate") },
        {
          name: "Collected Amount",
          color: "#90ed7d",
          // Scaled-down for display; raw value shown in tooltip via point.custom.raw
          data: items.map((item) => ({
            y: Math.round((item.collected_amount ?? 0) / collectedScale),
            custom: { raw: item.collected_amount ?? 0 },
          })),
        },
      ]);
    } catch (e) {
      console.warn("Recovery intent API error:", e.message);
    }
  }

  // ── Call List Table ──────────────────────────────────────────────────────────
  const [callsData, setCallsData] = useState([]);
  const [callsProducts, setCallsProducts] = useState([]);
  const [callSearchText, setCallSearchText] = useState("");
  const [callProductFilter, setCallProductFilter] = useState("");
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [callSelectionRange, setCallSelectionRange] = useState(null);

  const handleCallDateSelect = (ranges) => {
    setCallSelectionRange(ranges.selection);
  };

  const fetchDetailedCalls = async ({
    search,
    product,
    fromDate,
    toDate,
  } = {}) => {
    let url = "api/report/collection-info-detailed/";
    const parts = [`outcome=${encodeURIComponent("Complete Settlement")}`];
    if (fromDate && toDate) {
      parts.push(`from_date=${fromDate}`);
      parts.push(`to_date=${toDate}`);
    }
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    if (product) parts.push(`product=${encodeURIComponent(product)}`);
    url += `?${parts.join("&")}`;
    try {
      const res = await services.get(url);
      if (!res || res === "TypeError: Failed to fetch") return;
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      setCallsData(res.data || []);
      setCallsProducts(res.filters?.products || []);
    } catch (e) {
      console.warn("Recovery table API error:", e.message);
    }
  };

  // Load table on mount (last 7 days)
  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    fetchDetailedCalls({ fromDate: formatDMY(from), toDate: formatDMY(today) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const today = new Date();
    let fromDate, toDate;
    if (callSelectionRange) {
      fromDate = formatDMY(callSelectionRange.startDate);
      toDate = formatDMY(callSelectionRange.endDate);
    } else {
      const from = new Date(today);
      from.setDate(today.getDate() - 7);
      fromDate = formatDMY(from);
      toDate = formatDMY(today);
    }
    fetchDetailedCalls({
      search: callSearchText,
      product: callProductFilter,
      fromDate,
      toDate,
    });
  }, [callSearchText, callProductFilter, callSelectionRange]);

  const [day] = useState(7);

  // collection type params
  const COLLECTION_TYPES = [
    { label: "Pre Due Collection", value: "predue" },
    { label: "Post Due Collection >30 days", value: "postdue30" },
    { label: "Post Due Collection <30 days", value: "postdue60" },
    { label: "Late Collection >60 days", value: "postdue90" },
  ];
  const [collectionType, setCollectionType] = useState("");

  const handleCollectionTypeChange = (e) => {
    const val = e.target.value;
    setCollectionType(val);
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - day);
    // Pass val directly — state won't be updated yet at call time
    fetchRecoveryIntentData(formatDMY(from), formatDMY(today), val);
  };

  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - day);
    fetchRecoveryIntentData(formatDMY(from), formatDMY(today));
    if (!isAgentRole) fetchHierarchyPTP();
  }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Report filter state (non-agent roles) ──
  const [reportFilterWidth, setReportFilterWidth] = useState(0);
  const [isReportFilterOpen, setIsReportFilterOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({});

  const changeReportFilterWidth = () => {
    if (reportFilterWidth === 550) {
      setReportFilterWidth(0);
      setIsReportFilterOpen(false);
    } else {
      setReportFilterWidth(550);
      setIsReportFilterOpen(true);
    }
  };
  const handleReportFilterApply = (f) => setReportFilters(f);
  const handleReportFilterReset = () => setReportFilters({});

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const mapping = {
      lob: "lob_name",
      manager: "manager_name",
      team_lead: "team_lead_name",
      agent: "agent_name",
      dca_organization: "dca_organization_name",
      dca_manager: "dca_manager_name",
      dca_team: "dca_team_lead_name",
      dca_agent: "dca_agent_name",
      collection_strategy: "collection_strategy",
      product_type: "product_name",
      time_period: "period",
      from_date: "from_date",
      to_date: "to_date",
      bucket_type: "bucket_type",
    };
    for (const [key, value] of Object.entries(filters)) {
      if (mapping[key] && value) params.append(mapping[key], value);
      else if (value && !mapping[key]) params.append(key, value);
    }
    return params.toString().replace(/\+/g, "%20");
  };

  // ── Hierarchy PTP state (non-agent roles) ──
  const [hierDateLabels, setHierDateLabels] = useState([]);
  const [hierSeries, setHierSeries] = useState([]);
  const [hierCalls, setHierCalls] = useState([]);

  const fetchHierarchyPTP = async (filters = {}) => {
    const qs = buildQueryParams(filters);
    const url = `api/call_verification/hierarchy-promise-to-pay/${
      qs ? `?${qs}` : ""
    }`;
    const res = await services.get(url);
    if (!res || res === "TypeError: Failed to fetch") return;
    if (res.code === "token_not_valid") {
      storage.clear();
      history.push("/login");
      return;
    }
    const items = res.date_wise_data || [];
    setHierDateLabels(items.map((d) => d.date?.substring(0, 5)));
    setHierSeries([
      { name: "PTP Generated", data: items.map((d) => d.ptp_count ?? 0) },
      { name: "PTP Kept", data: items.map((d) => d.kept_count ?? 0) },
      {
        name: "PTP Broken",
        data: items.map((d) =>
          Math.max(0, (d.ptp_count ?? 0) - (d.kept_count ?? 0))
        ),
      },
    ]);
    setHierCalls(res.detailed_calls || []);
  };

  // Re-fetch when filters applied/reset
  useEffect(() => {
    if (!isAgentRole) fetchHierarchyPTP(reportFilters);
  }, [reportFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * PTP Trend bar chart config
   */
  const recoveryIntentChartOptions = {
    chart: {
      type: "column",
      height: "320px",
    },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: recoveryDateLabels,
      gridLineWidth: 0,
      labels: {
        rotation: recoveryDateLabels.length > 15 ? -45 : 0,
        style: { fontSize: "11px" },
      },
    },
    yAxis: {
      min: 0,
      gridLineColor: "transparent",
      title: { text: "Number of Calls" },
      labels: {
        // Show plain integers — no "25k" formatting
        formatter: function () {
          return this.value;
        },
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        let s = `<b>${this.x}</b><br/>`;
        this.points.forEach((pt) => {
          // For Collected Amount show the real value stored in point.custom.raw
          const displayVal =
            pt.series.name === "Collected Amount"
              ? (pt.point.options.custom?.raw ?? pt.y).toLocaleString()
              : pt.y;
          s += `<span style="color:${pt.color}">●</span> ${pt.series.name}: <b>${displayVal}</b><br/>`;
        });
        return s;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 3,
        groupPadding: 0.05,
        pointPadding: 0,
        dataLabels: { enabled: false },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: recoveryStackedSeries,
  };

  // Highcharts config for non-agent roles (PTP Generated / Kept / Broken)
  const hierChartOptions = {
    chart: { type: "column", height: "320px" },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: hierDateLabels,
      gridLineWidth: 0,
      tickInterval: Math.max(1, Math.floor(hierDateLabels.length / 8)),
    },
    yAxis: { min: 0, gridLineColor: "transparent", title: { text: "Count" } },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 3,
        groupPadding: 0.05,
        pointPadding: 0,
        dataLabels: { enabled: false },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: hierSeries,
  };

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />

      <div>
        <div className="container-fluid">
          <div className="my-call">
            <div
              className="side-panel-with-table clearfix"
              style={{ display: "flex" }}
            >
              <ReportNav />

              <div className="call-table" style={{ paddingTop: "0px" }}>
                {/* Active filter banner */}
                {!isAgentRole && (
                  <ReportFilterBanner
                    filters={reportFilters}
                    onClear={handleReportFilterReset}
                  />
                )}
                {isAgentRole ? (
                  <>
                    <div className="my-calls-column">
                      <div className="calls-top-pannel">
                        <div className="row">
                          <div className="col-lg-6">
                            <div className="pannel-heading clearfix">
                              <div className="pannel-heading-icon">
                                <i
                                  className="fa fa-snowflake-o"
                                  aria-hidden="true"
                                ></i>
                              </div>
                              <div className="pannel-heading-info">
                                {storage.getItem("usecase") ===
                                  "Third Party Collection Agency" ||
                                storage
                                  .getItem("usecase")
                                  .includes("Collections") ? (
                                  <h3>PTP Cycle</h3>
                                ) : (
                                  <h3>Opportunity </h3>
                                )}

                                {storage.getItem("usecase") ===
                                  "Third Party Collection Agency" ||
                                storage
                                  .getItem("usecase")
                                  .includes("Collections") ? (
                                  <p>Real time collection analysis</p>
                                ) : (
                                  <p>
                                    Opportunity generated and converted into
                                    Leads and wins
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4">
                      {/* ── Recovery Intent Bar Chart ── */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ paddingTop: "10px", paddingBottom: "10px" }}
                      >
                        {/* Collection type filter */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: "6px 12px 0",
                            alignItems: "center",
                          }}
                          className=" gap-4"
                        >
                          <p>Collection Stage : </p>
                          <select
                            value={collectionType}
                            onChange={handleCollectionTypeChange}
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "8px 14px",
                              fontSize: "13px",
                              cursor: "pointer",
                              minWidth: "220px",
                            }}
                          >
                            <option value="">All Collection Types</option>
                            {COLLECTION_TYPES.map((ct) => (
                              <option key={ct.value} value={ct.value}>
                                {ct.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={recoveryIntentChartOptions}
                        />
                      </div>
                      {/* call list section */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ marginTop: "8px", paddingBottom: "16px" }}
                      >
                        {/* Filter bar */}
                        <div
                          className="flex justify-end mb-4"
                          style={{
                            padding: "12px 0 0 0",
                            gap: "8px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {/* Search */}
                          <input
                            type="text"
                            placeholder="Search..."
                            value={callSearchText}
                            onChange={(e) => setCallSearchText(e.target.value)}
                            className="search-tag"
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              width: "200px",
                              outline: "none",
                              padding: "10px",
                            }}
                          />

                          {/* Date Range Picker */}
                          <div style={{ position: "relative" }}>
                            <button
                              onClick={() => setDateRangeOpen((o) => !o)}
                              style={{
                                border: "none",
                                background: "#eef2f5",
                                borderRadius: "10px",
                                outline: "none",
                                padding: "10px 14px",
                                fontSize: "13px",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {callSelectionRange
                                ? `${callSelectionRange.startDate.toLocaleDateString()} – ${callSelectionRange.endDate.toLocaleDateString()}`
                                : "Select Date Range"}
                            </button>
                            {dateRangeOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "44px",
                                  right: 0,
                                  zIndex: 999,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                }}
                              >
                                <DateRangePicker
                                  ranges={[
                                    callSelectionRange || {
                                      startDate: new Date(),
                                      endDate: new Date(),
                                      key: "selection",
                                    },
                                  ]}
                                  onChange={handleCallDateSelect}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    background: "#fff",
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      setCallSelectionRange(null);
                                      setDateRangeOpen(false);
                                    }}
                                    style={{
                                      border: "none",
                                      background: "#eef2f5",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    onClick={() => setDateRangeOpen(false)}
                                    style={{
                                      border: "none",
                                      background: "#270086",
                                      color: "#fff",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Apply
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Product */}
                          <select
                            value={callProductFilter}
                            onChange={(e) =>
                              setCallProductFilter(e.target.value)
                            }
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "10px",
                              fontSize: "13px",
                            }}
                          >
                            <option value="">All Products</option>
                            {callsProducts.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>

                          {/* Clear all */}
                          <button
                            onClick={() => {
                              setCallSearchText("");
                              setCallProductFilter("");
                              setCallSelectionRange(null);
                            }}
                            style={{
                              border: "none",
                              background: "#eef2f5",
                              borderRadius: "10px",
                              outline: "none",
                              padding: "10px",
                              fontSize: "13px",
                              cursor: "pointer",
                            }}
                          >
                            Clear
                          </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-y-scroll">
                          <table className="ss">
                            <tbody>
                              <tr id="black-line">
                                {[
                                  "Call Date",
                                  "Customer",
                                  "Agent",
                                  "Language",
                                  "Product",
                                  "Outcome",
                                  "Action",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>

                              {callsData.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="7"
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    No Record Found
                                  </td>
                                </tr>
                              ) : (
                                callsData.map((call, i) => (
                                  <tr key={i}>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.call_date}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-nowrap text-left text-gray-900">
                                        {call.customer_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.agent_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.language}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-no-wrap text-left text-gray-900">
                                        {call.product_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="whitespace-nowrap text-left text-gray-900">
                                        {call.outcome_raw || "—"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer">
                                      <a
                                        className="text-left text-gray-900 whitespace-nowrap"
                                        onClick={() =>
                                          call.call_id &&
                                          history.push(
                                            `/call/${call.call_id}/call-insight`
                                          )
                                        }
                                      >
                                        View More
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ── Non-agent / other roles UI ── */
                  <div className="my-calls-column">
                    <div className="calls-top-pannel">
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="pannel-heading clearfix">
                            <div className="pannel-heading-info">
                              <h3>PTP Cycle</h3>
                              <p>Real time collection analysis</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4">
                      {/* PTP chart */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ paddingTop: "10px", paddingBottom: "10px" }}
                      >
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={hierChartOptions}
                        />
                      </div>

                      {/* Calls table */}
                      <div
                        className="col-lg-12 bg-white"
                        style={{ marginTop: "8px", paddingBottom: "16px" }}
                      >
                        <div className="overflow-y-scroll">
                          <table className="ss">
                            <tbody>
                              <tr id="black-line">
                                {[
                                  "Call Date",
                                  "Agent",
                                  "Customer",
                                  "Product",
                                  "Collection Status",
                                  "PTP",
                                  "Collected",
                                  "Target",
                                  "Action",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                              {hierCalls.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan="8"
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    No Record Found
                                  </td>
                                </tr>
                              ) : (
                                hierCalls.map((call, i) => (
                                  <tr key={i}>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.call_date}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.agent_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.customer_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.product_name}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900 whitespace-nowrap">
                                        {call.collection_status}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs whitespace-nowrap">
                                      <p className="text-left text-gray-900">
                                        {call.ptp_status ? "Yes" : "No"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="text-left text-gray-900">
                                        {call.collected_amount_formatted ??
                                          call.collected_amount}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                      <p className="text-left text-gray-900">
                                        {call.target_amount_formatted ??
                                          call.target_amount ??
                                          "-"}
                                      </p>
                                    </td>
                                    <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer">
                                      <a
                                        className="text-left text-gray-900 whitespace-nowrap"
                                        onClick={() =>
                                          call.call_id &&
                                          history.push(
                                            `/call/${call.call_id}/call-insight`
                                          )
                                        }
                                      >
                                        View More
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Floating ReportFilter — non-agent roles only */}
      {!isAgentRole && (
        <>
          <button
            onClick={changeReportFilterWidth}
            title="Global Filters"
            style={{
              position: "fixed",
              transition: "0.5s",
              top: "30%",
              right: reportFilterWidth > 0 ? reportFilterWidth + 8 : 25,
              backgroundColor:
                Object.keys(reportFilters).length > 0 ? "#271078" : "#F99E52",
              color: "white",
              padding: "10px",
              height: "40px",
              width: "40px",
              zIndex: 2001,
              borderRadius: "10%",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            }}
          >
            <FaFilter size={18} />
            {Object.keys(reportFilters).length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  backgroundColor: "#F99E52",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {Object.keys(reportFilters).length}
              </span>
            )}
          </button>

          <ReportFilter
            width={reportFilterWidth}
            changeSideBarWidth={changeReportFilterWidth}
            onApplyFilter={handleReportFilterApply}
            onResetFilter={handleReportFilterReset}
            userRole={role}
          />

          {isReportFilterOpen && (
            <div
              onClick={changeReportFilterWidth}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                zIndex: 1999,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Recovery;
